import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Subscription,
  SubscriptionTier,
  SubscriptionAddon,
  UsageStatistics,
  TrialStatus,
  PlanChangePreview,
  PendingPlanChange,
} from '@core/models/subscription.model';
import { SubscriptionState, SubscriptionStore } from './subscription.store';

@Injectable({ providedIn: 'root' })
export class SubscriptionQuery extends Query<SubscriptionState> {
  constructor(protected store: SubscriptionStore) {
    super(store);
  }

  selectCurrentSubscription(): Observable<Subscription | null> {
    return this.select((state) => state.currentSubscription);
  }

  selectAvailableTiers(tierType?: 'gp' | 'lp' | 'all'): Observable<SubscriptionTier[]> {
    return this.select((state) => {
      if (tierType === 'gp') {
        return state.availableTiers.gp;
      } else if (tierType === 'lp') {
        return state.availableTiers.lp;
      }
      return state.availableTiers.all;
    });
  }

  selectAllAvailableTiers(): Observable<SubscriptionTier[]> {
    return this.select((state) => {
      const allTiers = [...state.availableTiers.gp, ...state.availableTiers.lp];
      const uniqueTiers = Array.from(new Map(allTiers.map(tier => [tier.uid, tier])).values());
      return uniqueTiers.length > 0 ? uniqueTiers : state.availableTiers.all;
    });
  }

  selectAvailableAddons(): Observable<SubscriptionAddon[]> {
    return this.select((state) => state.availableAddons);
  }

  selectUsageStatistics(month: number, year: number): Observable<UsageStatistics | null> {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    return this.select((state) => state.usageStatistics[key] || null);
  }

  selectLoading(): Observable<SubscriptionState['loading']> {
    return this.select((state) => state.loading);
  }

  selectCurrentSubscriptionLoading(): Observable<boolean> {
    return this.select((state) => state.loading.currentSubscription);
  }

  selectTiersLoading(): Observable<boolean> {
    return this.select((state) => state.loading.tiers);
  }

  selectAddonsLoading(): Observable<boolean> {
    return this.select((state) => state.loading.addons);
  }

  selectUsageLoading(): Observable<boolean> {
    return this.select((state) => state.loading.usage);
  }

  selectErrors(): Observable<SubscriptionState['errors']> {
    return this.select((state) => state.errors);
  }

  selectCurrentSubscriptionError(): Observable<string | null> {
    return this.select((state) => state.errors.currentSubscription);
  }

  selectHasCurrentSubscription(): Observable<boolean> {
    return this.selectCurrentSubscription().pipe(map((sub) => !!sub));
  }

  selectIsTrial(): Observable<boolean> {
    return this.selectCurrentSubscription().pipe(map((sub) => sub?.isTrialPeriod || false));
  }

  getCurrentSubscription(): Subscription | null {
    return this.getValue().currentSubscription;
  }

  getAvailableTiers(tierType?: 'gp' | 'lp' | 'all'): SubscriptionTier[] {
    const state = this.getValue();
    if (tierType === 'gp') {
      return state.availableTiers.gp;
    } else if (tierType === 'lp') {
      return state.availableTiers.lp;
    }
    return state.availableTiers.all;
  }

  selectTrialStatus(): Observable<TrialStatus | null> {
    return this.select((state) => state.trialStatus);
  }

  selectPlanChangePreview(): Observable<PlanChangePreview | null> {
    return this.select((state) => state.planChangePreview);
  }

  selectPendingPlanChange(): Observable<PendingPlanChange | null> {
    return this.select((state) => state.pendingPlanChange);
  }

  selectPlanChangeLoading(): Observable<boolean> {
    return this.select((state) => state.loading.planChange);
  }

  selectPlanChangeError(): Observable<string | null> {
    return this.select((state) => state.errors.planChange);
  }

  getTrialStatus(): TrialStatus | null {
    return this.getValue().trialStatus;
  }

  getPendingPlanChange(): PendingPlanChange | null {
    return this.getValue().pendingPlanChange;
  }
}
